/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useEffect, useState } from 'react';
import { includes } from 'lodash';

import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import FulfillmentAction from '@broadleaf/admin-components/dist/oms/components/FulfillmentAction';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import { queueFetch } from '@broadleaf/admin-components/dist/oms/hooks/useFufillmentState/state/fulfillmentActions';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import UpdateFulfillmentCommissionsModal from '../../UpdateFulfillmentCommissionsModal';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import {
  IFulfillmentSellerPaymentSummary,
  IOrderFulfillmentCommissions
} from '../../../utils/RmFulfillmentUtils';
import ManageSellerPaymentsModal from '@src/oms/FulfillmentView/ManageSellerPaymentsModal';

const logger = log.getLogger(
  'oms.components.FulfillmentView.action-components.ManageSellerPayments'
);

export interface StatusChangeProps {
  actionDefinition: IMetadataAction;
  dispatch: Function;
  metadata: IMetadata;
  state: IFulfillmentViewLocalState;
}

const ManageSellerPayments: React.FC<StatusChangeProps> = ({
  actionDefinition,
  dispatch,
  metadata,
  state
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const contextParams = useContextParams(metadata, state);
  const { attributes = {}, label } = actionDefinition;
  const {
    allowedStatuses = [],
    fulfillmentTypeAllowedStatuses = {},
    fulfillmentType = ''
  } = attributes as Record<string, any>;
  const handleClose = () => setIsOpen(false);
  const [paymentsLoaded, setPaymentsLoaded] = useState<boolean>(false);
  const readEndpoint = getEndpointByType(
    actionDefinition,
    'READ_SELLER_PAYMENTS'
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [
    sellerPayments,
    setSellerPayments
  ] = useState<IFulfillmentSellerPaymentSummary>(null);
  const allAllowedStatuses = allowedStatuses.concat(
    fulfillmentTypeAllowedStatuses[state.data.type]
  );

  const refreshSellerPayments = useCallback(async () => {
    if (loading || state.isFetching) {
      return;
    }
    try {
      setLoading(true);
      dispatch(queueFetch());
      const { data } = await request(
        { method: 'get', ...readEndpoint, data: state.data },
        contextParams
      );
      setSellerPayments(data);
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      console.log(
        `An error occurred trying to read the commissions for fulfillment ${contextParams.id}: ${err}`
      );
      logger.error(
        `An error occurred trying to read the commissions for fulfillment ${contextParams.id}: ${err}`
      );
    } finally {
      setLoading(false);
      setPaymentsLoaded(true);
    }
  }, [
    contextParams,
    readEndpoint,
    loading,
    state.data,
    state,
    dispatch,
    queueFetch
  ]);
  let labelText = label;
  if (!includes(allAllowedStatuses, state.data.status)) {
    labelText = 'View Seller Payments';
  }

  if (fulfillmentType !== '' && fulfillmentType !== state.data.type) {
    return null;
  }

  return (
    <>
      <FulfillmentAction
        color="black"
        disabled={state.isFetching || loading}
        onClick={async () => {
          await refreshSellerPayments();
          setIsOpen(true);
        }}
      >
        {labelText}
      </FulfillmentAction>

      {isOpen && sellerPayments && paymentsLoaded && (
        <ManageSellerPaymentsModal
          actionDefinition={actionDefinition}
          metadata={metadata}
          state={state}
          sellerPaymentSummary={sellerPayments}
          onClose={handleClose}
          refresh={refreshSellerPayments}
          title={labelText}
          allAllowedStatuses={allAllowedStatuses}
        />
      )}
    </>
  );
};

export default ManageSellerPayments;
